/**
 * Story-key distribution control message.
 *
 * When a user posts a story, the per-story 32-byte StoryKey must travel to
 * each chosen audience-member through that recipient's pairwise E2EE envelope.
 * The codec defined here produces the *plaintext that gets encrypted* — the
 * on-the-wire envelope is the standard `kaether:e2ee:v1:` form,
 * indistinguishable to the server from a chat message or a receipt by length
 * or prefix.
 *
 * Security consequences:
 *
 * 1. **Audience confidentiality is implicit.** The server never sees a list of
 *    "who is in the audience for story Y." Audience membership = "you have
 *    the key or you don't."
 *
 * 2. **Sender authentication piggybacks on E2EE.** The outer envelope is
 *    signed by the sender's identity key.
 *
 * 3. **Schema/length-validated decode.** A malformed payload (wrong schema,
 *    key not 32 bytes, empty storyId) is rejected at decode time.
 *
 * Wire format of the *plaintext that gets encrypted*:
 *
 *   kaether:story:share:v1:<base64url(JSON)>
 *
 * JSON shape:
 *   { "schema": 1, "storyId": "01H...", "storyKey": "<32-byte base64>",
 *     "metadata": { "schema": 1, "storyId": "01H...", "authorUserId": "alice",
 *                   "createdAt": 1700000000, "durationSeconds": 5,
 *                   "mimeType": "image/jpeg" } }
 *
 * Mirrors iOS `kaether_for_ios/Stoat/E2EE/StoryKeyDistribution.swift`.
 */

import type { StoryError, StoryMetadata } from "./Story";
import { STORY_METADATA_CURRENT_SCHEMA } from "./Story";

// ---------------------------------------------------------------------------
// Payload type
// ---------------------------------------------------------------------------

export interface StoryKeyDistributionPayload {
  /** Always 1. */
  readonly schema: number;
  readonly storyId: string;
  /** Base64-encoded 32-byte key (standard base64, not base64url). */
  readonly storyKey: string;
  readonly metadata: StoryMetadata;
}

/** Current distribution payload schema version. */
export const STORY_DISTRIBUTION_CURRENT_SCHEMA = 1;

// ---------------------------------------------------------------------------
// Codec
// ---------------------------------------------------------------------------

/** Wire-prefix for story-key distribution control messages. */
export const STORY_DISTRIBUTION_PREFIX = "kaether:story:share:v1:";

/**
 * Returns true if the plaintext looks like a story-key distribution.
 * Does not validate the payload — use `decode` for that.
 */
export function isStoryDistribution(s: string): boolean {
  return s.startsWith(STORY_DISTRIBUTION_PREFIX);
}

/**
 * Encodes a `StoryKeyDistributionPayload` to the wire string.
 *
 * Precondition: `storyKey` must be exactly 32 raw bytes (as base64, 44 chars).
 * Key length is checked against the decoded byte count, so a correctly-formed
 * 32-byte key is required.
 *
 * Mirrors iOS `StoryKeyDistributionCodec.encode(_:)`.
 */
export function encodeStoryDistribution(
  payload: StoryKeyDistributionPayload,
): string {
  // Validate key length on encode (defensive, mirrors iOS precondition).
  const keyBytes = base64Decode(payload.storyKey);
  if (keyBytes.length !== 32) {
    throw {
      kind: "invalidKeyLength",
      got: keyBytes.length,
    } satisfies StoryError;
  }
  const json = JSON.stringify(payload);
  return STORY_DISTRIBUTION_PREFIX + base64UrlEncode(new TextEncoder().encode(json));
}

/**
 * Decodes and validates a story-key distribution wire string.
 *
 * Throws `StoryError` on:
 * - Wrong prefix → `malformed`
 * - Invalid base64url → `malformed`
 * - JSON parse failure → `malformed`
 * - Wrong schema → `unsupportedSchema`
 * - Key not 32 bytes → `invalidKeyLength`
 * - Empty storyId → `malformed`
 * - `metadata.storyId` ≠ outer `storyId` → `malformed`
 * - `metadata.schema` ≠ 1 → `unsupportedSchema`
 *
 * Mirrors iOS `StoryKeyDistributionCodec.decode(_:)`.
 */
export function decodeStoryDistribution(
  s: string,
): StoryKeyDistributionPayload {
  if (!s.startsWith(STORY_DISTRIBUTION_PREFIX)) {
    throw {
      kind: "malformed",
      reason: "not a story-key distribution",
    } satisfies StoryError;
  }
  const b64 = s.slice(STORY_DISTRIBUTION_PREFIX.length);
  let jsonBytes: Uint8Array;
  try {
    jsonBytes = base64UrlDecode(b64);
  } catch {
    throw {
      kind: "malformed",
      reason: "invalid base64url",
    } satisfies StoryError;
  }
  let payload: StoryKeyDistributionPayload;
  try {
    payload = JSON.parse(new TextDecoder().decode(jsonBytes));
  } catch {
    throw {
      kind: "malformed",
      reason: "json decode failed",
    } satisfies StoryError;
  }
  if (payload.schema !== STORY_DISTRIBUTION_CURRENT_SCHEMA) {
    throw {
      kind: "unsupportedSchema",
      schema: payload.schema,
    } satisfies StoryError;
  }
  const keyBytes = base64Decode(payload.storyKey);
  if (keyBytes.length !== 32) {
    throw {
      kind: "invalidKeyLength",
      got: keyBytes.length,
    } satisfies StoryError;
  }
  if (!payload.storyId || payload.storyId.length === 0) {
    throw { kind: "malformed", reason: "empty storyId" } satisfies StoryError;
  }
  // Cross-field consistency check.
  if (payload.metadata.storyId !== payload.storyId) {
    throw {
      kind: "malformed",
      reason: "metadata.storyId != storyId",
    } satisfies StoryError;
  }
  if (payload.metadata.schema !== STORY_METADATA_CURRENT_SCHEMA) {
    throw {
      kind: "unsupportedSchema",
      schema: payload.metadata.schema,
    } satisfies StoryError;
  }
  return payload;
}

/**
 * Convenience: returns null for non-distribution plaintexts. Throws only
 * when the payload claimed to be a distribution but was malformed.
 *
 * Mirrors iOS `StoryKeyDistributionCodec.tryDecode(_:)`.
 */
export function tryDecodeStoryDistribution(
  s: string,
): StoryKeyDistributionPayload | null {
  if (!isStoryDistribution(s)) return null;
  return decodeStoryDistribution(s);
}

// ---------------------------------------------------------------------------
// Base64url helpers (no padding, URL-safe) — mirrors iOS implementations
// ---------------------------------------------------------------------------

function base64UrlEncode(data: Uint8Array): string {
  // btoa on a byte string, then make URL-safe and strip padding.
  const binStr = Array.from(data)
    .map((b) => String.fromCharCode(b))
    .join("");
  return btoa(binStr)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function base64UrlDecode(s: string): Uint8Array {
  // Restore standard base64.
  let b64 = s.replace(/-/g, "+").replace(/_/g, "/");
  const pad = (4 - (b64.length % 4)) % 4;
  b64 += "=".repeat(pad);
  const binStr = atob(b64);
  const bytes = new Uint8Array(binStr.length);
  for (let i = 0; i < binStr.length; i++) {
    bytes[i] = binStr.charCodeAt(i);
  }
  return bytes;
}

/** Decode standard base64 (not base64url). Used to validate storyKey length. */
function base64Decode(s: string): Uint8Array {
  // Accept both standard and url-safe variants.
  let b64 = s.replace(/-/g, "+").replace(/_/g, "/");
  const pad = (4 - (b64.length % 4)) % 4;
  b64 += "=".repeat(pad);
  const binStr = atob(b64);
  const bytes = new Uint8Array(binStr.length);
  for (let i = 0; i < binStr.length; i++) {
    bytes[i] = binStr.charCodeAt(i);
  }
  return bytes;
}
